import { z } from 'zod';
import type { RouteObject } from 'react-router';
import { productId as productIdSchema } from '@lumlab/platform-core';
import type { Catalog, Language } from '@lumlab/platform-i18n';

export interface ProductNavigationItem {
    /** Absolute path in the app, under the product's prefix: `/tenders/agencies`. */
    path: string;
    labelKey: string;
}

/** What a product brings to the web app: its screens, its navigation and its interface texts. */
export interface ProductWeb {
    id: string;
    navigation: readonly ProductNavigationItem[];
    /** Mounted under `/<product id>`. */
    routes: RouteObject[];
    /** Mounted under `/admin/products/<product id>`, behind the global administration guard. */
    administration?: { routes: RouteObject[] };
    catalogs: Partial<Record<Language, Catalog>>;
}

export function defineProductWeb(definition: ProductWeb): ProductWeb {
    productIdSchema.parse(definition.id);
    for (const item of definition.navigation) {
        if (!item.path.startsWith(`/${definition.id}`)) {
            throw new Error(`Navigation path ${item.path} must start with /${definition.id}`);
        }
    }
    return definition;
}

/** An API failure, carrying the stable error code the interface translates (`platform.errors.<code>`). */
export class ApiError extends Error {
    readonly code: string;
    readonly status: number;
    readonly body: unknown;

    constructor(code: string, status: number, body?: unknown) {
        super(`API error ${status}: ${code}`);
        this.name = 'ApiError';
        this.code = code;
        this.status = status;
        this.body = body;
    }
}

/** Calls the LumLab API on the same origin, with the session cookie. */
export async function apiRequest<T>(
    path: string,
    options: { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
    let response: Response;
    try {
        response = await fetch(path, {
            method: options.method ?? 'GET',
            credentials: 'same-origin',
            headers: options.body === undefined ? {} : { 'content-type': 'application/json' },
            body: options.body === undefined ? null : JSON.stringify(options.body),
            signal: options.signal ?? null,
        });
    } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
            throw error;
        }
        throw new ApiError('network', 0);
    }
    if (!response.ok) {
        const body = z
            .object({ error: z.object({ code: z.string() }).passthrough() })
            .safeParse(await response.json().catch(() => null));
        throw new ApiError(
            body.success ? body.data.error.code : 'internal',
            response.status,
            body.success ? body.data : undefined,
        );
    }
    return (response.status === 204 ? undefined : await response.json()) as T;
}
